// src/lib/addFunds.js
//
// The Add Funds modal's money handling (mission 005, packet 01b §6–7).
//   shortfall   required / available / shortfall, ONE subtraction. Required
//               comes from the refusal (`funds.required_cents`, or `needed`)
//               or the caller's own quote; available from the refusal's
//               fresh summary (which nets disputed holds) when there is one,
//               else the refusal's `available`.
//               A refusal's raw `balance` includes held funds and is never
//               used as available.
//   fundsCopy   the sentence the modal shows (USD, never "credits")
//   addFundsSession  onFunded fires once, for a credited order of the SAME
//               account and network, and only while the caller is mounted.
//               It carries no purchase: callers refresh and re-quote, and a
//               purchase always needs another explicit confirmation.

import { formatUsd } from './usd.js';

const cents = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.ceil(v) : null);

export function shortfall({ error = null, required_cents = null, summary = null } = {}) {
  const f = error?.funds ?? null;
  const required = cents(f?.required_cents) ?? cents(error?.needed) ?? cents(required_cents);
  // the refusal keeps the REQUIRED amount; a later summary is the CURRENT availability (01d §5)
  const available = cents(summary?.available_cents) ?? cents(f?.available_cents) ?? cents(error?.available);
  return {
    required_cents: required,
    available_cents: available,
    shortfall_cents: required !== null && available !== null ? Math.max(0, required - available) : null,
  };
}

export function fundsCopy({ shortfall_cents = null, code = null, frozen = false } = {}) {
  if (frozen || code === 'frozen') return 'Payments are paused for a moment. Please try again later.';
  if (code === 'disputed_hold') {
    return 'Part of your balance is on hold while a payment is reviewed. Adding funds can cover this; the held part is not available.';
  }
  if (shortfall_cents === null || shortfall_cents === undefined) return 'Add funds to your balance to continue.';
  if (shortfall_cents === 0) return 'Your balance covers this now. Go back and try again; the price is checked again.';
  return `You need ${formatUsd(shortfall_cents)} more.`;
}

const lower = (v) => (typeof v === 'string' ? v.toLowerCase() : v ?? null);

export function addFundsSession({ account = null, networkId = null, context = null, onFunded = null } = {}) {
  let fired = false, mounted = true;
  return {
    funded: () => fired,
    /** an order's phase, with the account and network it belongs to */
    orderPhase(phase, { account: acc = null, networkId: net = null, orderId = null } = {}) {
      if (fired || !mounted || phase !== 'credited') return;
      if (lower(acc) !== lower(account) || net !== networkId) return;
      fired = true;
      onFunded?.({ context, orderId });
    },
    callerUnmounted() { mounted = false; },
    /** the modal closed or the account changed: never call back again */
    dispose() { mounted = false; },
  };
}

/** the welcome-offer bonus a deposit of `cents` earns (mission 008, decision
 *  008-02): "d % off" — the server's arithmetic, floor(cents × d / (100 − d)),
 *  within the remaining bonus (summary.promo.remaining_cents, net of the cap,
 *  the budget and open reservations), for deposits of at least the minimum;
 *  0 when the summary's promo is not eligible */
export function welcomeBonusCents(promo, cents) {
  if (!promo?.eligible || !Number.isFinite(cents) || cents <= 0) return 0;
  const d = promo.discount_percent;
  if (!Number.isInteger(d) || d < 1 || d > 90) return 0;
  if (cents < (promo.min_deposit_cents ?? 0)) return 0;
  return Math.max(0, Math.min(Math.floor((cents * d) / (100 - d)), promo.remaining_cents ?? 0));
}

/** the TRUE discount on the credits a deposit buys, in whole percent: the
 *  configured one while uncapped, less once the cap clips it ($25 → $35: 28, never rounded up) */
export function trueDiscountPct(promo, cents) {
  const bonus = welcomeBonusCents(promo, cents);
  // FLOORED, in integers: a displayed claim never exceeds the effective discount
  return bonus > 0 ? Math.floor((bonus * 100) / (cents + bonus)) : 0;
}

/** the offer's cards: best_amount_cents (the deposit that earns the whole
 *  remaining bonus) and 40 % / 60 % of it rounded to whole dollars — kept only
 *  when at least the minimum, below the best, and EXACT (the bonus has no
 *  rounding: the card states the configured discount). Best last. */
export function welcomeCards(promo) {
  const best = promo?.best_amount_cents;
  if (!promo?.eligible || !Number.isFinite(best) || best <= 0) return [];
  const d = promo.discount_percent;
  const min = promo.min_deposit_cents ?? 0;
  const pays = [];
  for (const f of [0.4, 0.6]) {
    const p = Math.round((best * f) / 100) * 100;
    if (p >= min && p < best && (p * d) % (100 - d) === 0 && !pays.includes(p)) pays.push(p);
  }
  pays.push(best);
  return pays.map((pay) => {
    const bonus = welcomeBonusCents(promo, pay);
    return { pay_cents: pay, credits_cents: pay + bonus, discount_pct: trueDiscountPct(promo, pay), best: pay === best };
  }).filter((c) => c.credits_cents > c.pay_cents);
}
